import { Button, Drawer, Form, Input, notification, Popover, Select } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useDeleteFaqCategoryMutation, useUpdateFaqCategoryMutation } from 'app/services/edpApiV1/faq';
import { DEFAULT_CATEGORY_VALUE } from 'shared/constants/faq';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { SimpleIconName, resolveGlyph } from '../../../shared/components/icons/icons';
import ListOfIcons from '../createCategory/listOfIcons/ListOfIcons';
import style from './index.module.scss';
import AcceptDeleteCategoryModal from './accept-delete-category/AcceptDeleteCategoryModal';

type TProps = {
  onClose: () => void;
  open: boolean;
  title?: string;
  icon?: SimpleIconName;
  id?: number | typeof DEFAULT_CATEGORY_VALUE;
};

const UpdateCategoryDrawer: FC<TProps> = ({ onClose, open, title: titleProp, icon: iconProp, id: idProp }) => {
  const [title, setTitle] = useState<string>();
  const [id, setId] = useState<number | typeof DEFAULT_CATEGORY_VALUE>();
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const [acceptDeleteModalOpen, setAcceptDeleteModalOpen] = useState<boolean>(false);
  const [currentIcon, setCurrentIcon] = useState<SimpleIconName | undefined>();
  const [updateFaqCategory, { isLoading: isUpdateLoading }] = useUpdateFaqCategoryMutation();
  const [deleteFaqCategory, { isError: deleteIsError, error: deleteError, isLoading: isDeleteLoading }] =
    useDeleteFaqCategoryMutation();

  useEffect(() => {
    if (deleteIsError && deleteError) {
      setAcceptDeleteModalOpen(true);
    }
  }, [deleteIsError, deleteError]);

  useEffect(() => {
    if (titleProp) {
      setTitle(titleProp);
    }
  }, [titleProp]);

  useEffect(() => {
    if (iconProp) {
      setCurrentIcon(iconProp);
    }
  }, [iconProp]);

  useEffect(() => {
    if (idProp) {
      setId(idProp);
    }
  }, [idProp]);

  const onCloseClick = () => {
    onClose();
  };

  const onDeleteClick = async () => {
    try {
      if (id) {
        await deleteFaqCategory({ id }).unwrap();
        notification.success({ message: 'Категория удалена' });
      } else {
        notification.error({ message: 'Категория не найдена' });
      }
    } catch (e: any) {
      if (e?.data?.statusCode !== 403) {
        notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
      }
    }

    onClose();
  };

  const onSubmit = async () => {
    try {
      await updateFaqCategory({ id: id!, icon: currentIcon!, name: title! });
      notification.success({ message: 'Категория отредактирована' });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    onClose();
  };

  const onIconChange = (data: any) => {
    setCurrentIcon(data);
  };

  const handleOpenChange = () => {
    setOpenPopover(prev => !prev);
  };

  const onIconClick = (icon: SimpleIconName) => {
    setCurrentIcon(icon);
    setOpenPopover(false);
  };

  const onAcceptDeleting = async () => {
    try {
      if (id) {
        await deleteFaqCategory({ id, immediately: true }).unwrap();
        notification.success({ message: 'Категория удалена' });
      } else {
        notification.error({ message: 'Категория не найдена' });
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    onClose();
  };

  return (
    <Drawer
      title="Редактирование категории"
      onClose={onClose}
      open={open}
      width={720}
      extra={
        <div className={style['action-btns']}>
          <AcceptDeleteCategoryModal
            acceptDeleteModalOpen={acceptDeleteModalOpen}
            setAcceptDeleteModalOpen={setAcceptDeleteModalOpen}
            onDelete={onAcceptDeleting}
          />
          {id != 'null' && id != null && (
            <Button onClick={onDeleteClick} loading={isDeleteLoading}>
              Удалить
            </Button>
          )}
          <Button onClick={onCloseClick}>Отмена</Button>
          <Button onClick={onSubmit} type="primary" loading={isUpdateLoading} disabled={!title || !currentIcon}>
            Ок
          </Button>
        </div>
      }
    >
      <h4>Наименование категории</h4>
      <Input placeholder="Введите наименование" value={title} onChange={e => setTitle(e.target.value)} />
      <Popover
        content={<ListOfIcons onClick={onIconClick} />}
        title="Иконки"
        placement="topRight"
        trigger="click"
        open={openPopover}
        onOpenChange={handleOpenChange}
      >
        <div className={style['icon-select']}>
          <h4>Иконка</h4>
          <Select
            style={{ width: 70, color: '#1677ff' }}
            className="icon-select-elem"
            value={currentIcon ? resolveGlyph(currentIcon) : undefined}
            onChange={onIconChange}
            dropdownStyle={{ display: 'none' }}
          />
        </div>
      </Popover>
    </Drawer>
  );
};

export default UpdateCategoryDrawer;
